import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  GENRES,
  GENRE_LIST,
  numJP,
  type AgeRating,
  type GenreKey,
  type Work,
} from "../data";
import type { Nav, Route, SortKey } from "../types";
import { Empty, Footer, LoadingBlock, WorkCard } from "../components/atoms";
import { useFetch } from "../hooks";
import { fetchTags, listWorks } from "../api";
import { useT } from "../i18n";

interface Props {
  route: Route & { page: "search" };
  nav: Nav;
  query: string;
  setQuery: (q: string) => void;
}

const PRICE_KEYS = ["free", "p1", "p2", "p3"] as const;
type PriceKey = (typeof PRICE_KEYS)[number];
const AGE_LIST: AgeRating[] = ["all", "r15", "r18"];

function readList(sp: URLSearchParams, key: string): string[] {
  const raw = sp.get(key);
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

export function SearchPage({ route, query, setQuery, nav }: Props) {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedGenres = useMemo(() => {
    const arr = readList(searchParams, "genres") as GenreKey[];
    const legacy = searchParams.get("genre") as GenreKey | null;
    if (legacy && !arr.includes(legacy)) arr.push(legacy);
    return arr;
  }, [searchParams]);
  const selectedPriceRanges = useMemo(
    () => readList(searchParams, "prices"),
    [searchParams],
  );
  const selectedAges = useMemo(
    () => readList(searchParams, "ages") as AgeRating[],
    [searchParams],
  );
  const selectedTags = useMemo(() => {
    const arr = readList(searchParams, "tags");
    const legacy = searchParams.get("tag");
    if (legacy && !arr.includes(legacy)) arr.push(legacy);
    return arr;
  }, [searchParams]);
  const sort = (searchParams.get("sort") as SortKey) ?? "popular";

  function writeParams(patch: Record<string, string | string[] | null | undefined>) {
    const sp = new URLSearchParams(searchParams);
    sp.delete("genre");
    sp.delete("tag");
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || (Array.isArray(v) && v.length === 0) || v === "") {
        sp.delete(k);
      } else if (Array.isArray(v)) {
        sp.set(k, v.join(","));
      } else {
        sp.set(k, v);
      }
    }
    setSearchParams(sp, { replace: true });
  }
  function toggleInList<T extends string>(key: string, current: T[], v: T) {
    const next = current.includes(v) ? current.filter((x) => x !== v) : [...current, v];
    writeParams({ [key]: next });
  }
  function setSort(s: SortKey) {
    writeParams({ sort: s === "popular" ? null : s });
  }
  function clearAll() {
    setSearchParams(new URLSearchParams(), { replace: true });
    setQuery("");
  }

  const hasFilters =
    selectedGenres.length > 0 ||
    selectedPriceRanges.length > 0 ||
    selectedAges.length > 0 ||
    selectedTags.length > 0 ||
    !!query;

  // タグ一覧
  const tagsFetch = useFetch(fetchTags, []);

  // 検索結果 (フィルタ変更で再フェッチ)
  const [results, setResults] = useState<Work[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  useEffect(() => {
    let canceled = false;
    setResultsLoading(true);
    listWorks({
      q: query || undefined,
      genres: selectedGenres,
      tags: selectedTags,
      prices: selectedPriceRanges,
      ages: selectedAges,
      sort,
      limit: 60,
    })
      .then((ws) => {
        if (!canceled) setResults(ws);
      })
      .catch(() => {
        if (!canceled) setResults([]);
      })
      .finally(() => {
        if (!canceled) setResultsLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [query, selectedGenres, selectedTags, selectedPriceRanges, selectedAges, sort]);

  const tagsForCloud = tagsFetch.data ?? [];
  const maxTagCount = Math.max(...tagsForCloud.map((t) => t.count), 1);
  function tagSize(c: number): string {
    const r = c / maxTagCount;
    if (r > 0.8) return "s5";
    if (r > 0.55) return "s4";
    if (r > 0.35) return "s3";
    if (r > 0.18) return "s2";
    return "s1";
  }

  const heading = query ? (
    <>{t("search.heading.query", { query })}</>
  ) : selectedGenres.length === 1 ? (
    <>
      {t("search.heading.genre", {
        genre: GENRES[selectedGenres[0]!]?.ja ?? selectedGenres[0]!,
      })}
    </>
  ) : selectedTags.length === 1 ? (
    <>{t("search.heading.tag", { tag: selectedTags[0]! })}</>
  ) : (
    t("search.heading.noQuery")
  );

  // route prop はバックワード互換性のみ
  void route;

  return (
    <div className="page page-search">
      <div className="container">
        <div className="search-layout">
          <aside className="search-side">
            <div className="filter-group">
              <h3>{t("search.filterGenre")}</h3>
              {GENRE_LIST.filter((g) => g.key !== "all").map((g) => (
                <label className="check-row" key={g.key}>
                  <input
                    type="checkbox"
                    checked={selectedGenres.includes(g.key as GenreKey)}
                    onChange={() =>
                      toggleInList("genres", selectedGenres, g.key as GenreKey)
                    }
                  />
                  <span>{g.ja}</span>
                </label>
              ))}
            </div>
            <div className="filter-group">
              <h3>{t("search.filterPrice")}</h3>
              {(PRICE_KEYS as readonly PriceKey[]).map((p) => (
                <label className="check-row" key={p}>
                  <input
                    type="checkbox"
                    checked={selectedPriceRanges.includes(p)}
                    onChange={() => toggleInList("prices", selectedPriceRanges, p)}
                  />
                  <span>
                    {p === "free"
                      ? t("search.priceFree")
                      : p === "p1"
                        ? t("search.priceP1")
                        : p === "p2"
                          ? t("search.priceP2")
                          : t("search.priceP3")}
                  </span>
                </label>
              ))}
            </div>
            <div className="filter-group">
              <h3>{t("search.filterAge")}</h3>
              {AGE_LIST.map((a) => (
                <label className="check-row" key={a}>
                  <input
                    type="checkbox"
                    checked={selectedAges.includes(a)}
                    onChange={() => toggleInList<AgeRating>("ages", selectedAges, a)}
                  />
                  <span>
                    {a === "all"
                      ? t("search.ageAll")
                      : a === "r15"
                        ? t("search.ageR15")
                        : t("search.ageR18")}
                  </span>
                </label>
              ))}
            </div>
            <div className="filter-group">
              <h3>{t("search.filterTags")}</h3>
              <div className="tag-cloud">
                {tagsForCloud.map((tag) => (
                  <span
                    key={tag.name}
                    className={`tc ${tagSize(tag.count)} ${
                      selectedTags.includes(tag.name) ? "active" : ""
                    }`}
                    onClick={() => toggleInList("tags", selectedTags, tag.name)}
                    title={`${tag.count}`}
                  >
                    #{tag.name}
                  </span>
                ))}
              </div>
              {selectedTags.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 0 0",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--muted-2)",
                      fontFamily: "var(--font-mono)",
                      marginBottom: 6,
                    }}
                  >
                    {t("search.selected")}
                  </div>
                  <div className="tag-list">
                    {selectedTags.map((tg) => (
                      <span
                        key={tg}
                        className="tag tag-active"
                        onClick={() => toggleInList("tags", selectedTags, tg)}
                      >
                        #{tg} ×
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {hasFilters && (
              <button className="btn btn-sm btn-block" onClick={clearAll}>
                {t("search.clearAll")}
              </button>
            )}
          </aside>

          <main>
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <h1 style={{ margin: 0, fontSize: 18, color: "var(--text)" }}>{heading}</h1>
            </div>
            <div className="search-results-head">
              <span className="count">
                {t("search.resultsCount", { count: numJP(results.length) })}
              </span>
              <div className="sort-tabs">
                {(
                  [
                    { k: "popular", l: t("search.sortPopular") },
                    { k: "new", l: t("search.sortNew") },
                    { k: "rating", l: t("search.sortRating") },
                    { k: "price-asc", l: t("search.sortPriceAsc") },
                  ] as Array<{ k: SortKey; l: string }>
                ).map((s) => (
                  <button
                    key={s.k}
                    className={`stab ${sort === s.k ? "active" : ""}`}
                    onClick={() => setSort(s.k)}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
            </div>

            {resultsLoading ? (
              <LoadingBlock height={300} />
            ) : results.length > 0 ? (
              <>
                <div className="grid-3" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                  {results.map((w, i) => (
                    <WorkCard
                      key={w.id}
                      work={w}
                      onClick={(x) => nav({ page: "work", id: x.id })}
                      seed={i + 60}
                    />
                  ))}
                </div>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 24,
                    color: "var(--muted-2)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                  }}
                >
                  {t("search.loadMore")}
                </div>
              </>
            ) : (
              <Empty
                title={t("search.empty")}
                desc={t("search.emptyDesc")}
                action={
                  <button className="btn btn-sm" onClick={clearAll}>
                    {t("search.clearAll")}
                  </button>
                }
              />
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}
